/**
 * AI Communication Coach - Unified Hub
 * Combines: Communication Coach, AI Coaching Analytics, Homework AI
 * 
 * Tabs:
 * 1. Dashboard - Progress overview, current phase, badges, leaderboard
 * 2. Practice Sessions - Voice chat, assessments, AI feedback
 * 3. Weekly Tasks - Auto-generated + admin-assigned tasks
 * 4. Analytics - Score trends, skill breakdown, calls analysis
 * 5. Achievements - Badges, challenges, streaks
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  // Communication Coach APIs
  getCommunicationProfile,
  getCommunicationSessions,
  createCommunicationSession,
  sendCoachMessage,
  transcribeAudio,
  completeCommunicationSession,
  getCommunicationProgress,
  uploadSessionRecording,
  getCommunicationAnalytics,
  getUserFeedbackContext,
  // Gamification APIs
  getUserBadges,
  getUserChallenges,
  checkBadges,
  updateChallengeProgress,
  getBadgesLeaderboard,
  // Homework APIs
  getAdminHomeworkOverview,
  getAdminUserHomework,
  deleteHomeworkTask,
  assignHomeworkFromCoaching,
  // AI Coaching APIs
  getCompanyCoachingStats,
  getAICoachingPlan,
  getUsers,
  getDepartments,
  // Reports APIs
  getCoachReportsSummary,
  exportCoachReportsCSV,
  exportCoachReportsPDF,
  getUserDetailedReport
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Mic,
  MicOff,
  Volume2,
  Play,
  Pause,
  Send,
  Phone,
  Target,
  TrendingUp,
  TrendingDown,
  Trophy,
  Clock,
  CheckCircle2,
  CheckCircle,
  Circle,
  Star,
  Sparkles,
  Brain,
  MessageCircle,
  ChevronRight,
  RefreshCw,
  StopCircle,
  Loader2,
  BarChart3,
  Award,
  Zap,
  ListChecks,
  BookOpen,
  Medal,
  Crown,
  Gem,
  Flame,
  Shield,
  Eye,
  Rocket,
  Swords,
  Gift,
  Users,
  Building2,
  AlertCircle,
  AlertTriangle,
  Calendar,
  ClipboardCheck,
  GraduationCap,
  ArrowUp,
  ArrowDown,
  PieChart,
  Trash2,
  Filter,
  X,
  FileText,
  Download,
  Printer
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar
} from 'recharts';
import { useTheme } from '@/contexts/ThemeContext';
import { format } from 'date-fns';

// ==================== CONSTANTS ====================

const COACHING_PHASES = [
  { phase: 1, name: "Assessment", description: "Baseline evaluation", icon: Target, color: "bg-slate-500" },
  { phase: 2, name: "Foundation", description: "Build core skills", icon: Brain, color: "bg-blue-500" },
  { phase: 3, name: "Development", description: "Strengthen weaknesses", icon: TrendingUp, color: "bg-indigo-500" },
  { phase: 4, name: "Refinement", description: "Polish techniques", icon: Sparkles, color: "bg-purple-500" },
  { phase: 5, name: "Professional", description: "Advanced practice", icon: Star, color: "bg-amber-500" },
  { phase: 6, name: "Mastery", description: "Excellence achieved", icon: Trophy, color: "bg-emerald-500" },
];

const SKILL_LABELS = {
  pronunciation: "Pronunciation",
  pace: "Pace",
  clarity: "Clarity",
  confidence: "Confidence",
  comprehension: "Comprehension"
};

const BADGE_ICONS = {
  rocket: Rocket, star: Star, medal: Medal, award: Award, trophy: Trophy, crown: Crown,
  "trending-up": TrendingUp, zap: Zap, sparkles: Sparkles, gem: Gem, flame: Flame, fire: Flame,
  lightning: Zap, bolt: Zap, mic: Mic, clock: Clock, eye: Eye, shield: Shield, brain: Brain,
  "arrow-up": ArrowUp, "chart-line": TrendingUp, target: Target, bullseye: Target, sword: Swords
};

// ==================== MAIN COMPONENT ====================

export default function AICommCoach() {
  const { user, tenantId } = useOutletContext();
  const navigate = useNavigate();
  const { theme, isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  
  // Dashboard data
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [feedbackContext, setFeedbackContext] = useState(null);
  
  // Gamification data
  const [badges, setBadges] = useState({ earned_badges: [], progress: [] });
  const [challenges, setChallenges] = useState({ admin_challenges: [], auto_challenges: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [newBadges, setNewBadges] = useState([]);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);
  
  // Practice Session state
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionTimer, setSessionTimer] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [sessionResults, setSessionResults] = useState(null);
  
  // Refs for audio
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const fullRecordingRef = useRef([]);
  const messagesEndRef = useRef(null);
  
  // Homework/Tasks data
  const [homeworkOverview, setHomeworkOverview] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  
  // Analytics data (AI Coaching)
  const [coachingStats, setCoachingStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedCoachingUser, setSelectedCoachingUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [filters, setFilters] = useState({
    user_id: 'all',
    department_id: 'all'
  });
  
  // Reports data
  const [reportsSummary, setReportsSummary] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({ start: '', end: '' });
  const [selectedReportUser, setSelectedReportUser] = useState(null);
  const [userDetailedReport, setUserDetailedReport] = useState(null);
  const [exportingReport, setExportingReport] = useState(false);

  // ==================== EFFECTS ====================
  
  useEffect(() => {
    loadInitialData();
  }, []);
  
  useEffect(() => {
    // Update URL when tab changes
    setSearchParams({ tab: activeTab });
  }, [activeTab]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  useEffect(() => {
    if (activeSession && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setSessionTimer(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [activeSession]);

  // ==================== DATA LOADING ====================
  
  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      // Load all data in parallel
      const [
        profileRes, sessionsRes, progressRes, analyticsRes,
        badgesRes, challengesRes, leaderboardRes,
        homeworkRes, coachingRes, usersRes, deptsRes, feedbackCtxRes
      ] = await Promise.allSettled([
        getCommunicationProfile(),
        getCommunicationSessions(null, 10),
        getCommunicationProgress(),
        getCommunicationAnalytics(),
        getUserBadges(),
        getUserChallenges(),
        getBadgesLeaderboard(),
        getAdminHomeworkOverview(),
        getCompanyCoachingStats({}),
        getUsers(),
        getDepartments(),
        getUserFeedbackContext()
      ]);
      
      // Set data from successful responses
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data);
      if (sessionsRes.status === 'fulfilled') setSessions(sessionsRes.value.data.sessions || []);
      if (progressRes.status === 'fulfilled') setProgress(progressRes.value.data);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
      if (badgesRes.status === 'fulfilled') setBadges(badgesRes.value.data || { earned_badges: [], progress: [] });
      if (challengesRes.status === 'fulfilled') setChallenges(challengesRes.value.data || { admin_challenges: [], auto_challenges: [] });
      if (leaderboardRes.status === 'fulfilled') setLeaderboard(leaderboardRes.value.data?.leaderboard || []);
      if (homeworkRes.status === 'fulfilled') setHomeworkOverview(homeworkRes.value.data);
      if (coachingRes.status === 'fulfilled') setCoachingStats(coachingRes.value.data);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data || []);
      if (deptsRes.status === 'fulfilled') setDepartments(deptsRes.value.data || []);
      if (feedbackCtxRes.status === 'fulfilled') setFeedbackContext(feedbackCtxRes.value.data?.feedback_context || null);
      
      // Check for new badges
      try {
        const checkRes = await checkBadges();
        if (checkRes.data?.new_badges?.length > 0) {
          setNewBadges(checkRes.data.new_badges);
          setShowBadgeDialog(true);
        }
      } catch (e) { /* ignore */ }
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };
  
  const refreshData = async () => {
    await loadInitialData();
    toast.success('Data refreshed');
  };

  // ==================== PRACTICE SESSION FUNCTIONS ====================
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const startSession = async (type = 'practice') => {
    setIsSessionLoading(true);
    try {
      const res = await createCommunicationSession(type);
      setActiveSession(res.data.session);
      setMessages([{ role: 'assistant', content: res.data.initial_message }]);
      setSessionTimer(0);
      fullRecordingRef.current = [];
      
      if (res.data.audio) {
        playAudio(res.data.audio);
      }
      
      toast.success(`${type === 'assessment' ? 'Assessment' : 'Practice session'} started!`);
    } catch (error) {
      console.error('Failed to start session:', error);
      toast.error('Failed to start session');
    } finally {
      setIsSessionLoading(false);
    }
  };
  
  const sendMessage = async (message) => {
    if (!message.trim() || !activeSession) return;
    
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setInputMessage('');
    setIsSessionLoading(true);
    
    try {
      const res = await sendCoachMessage(
        activeSession.id,
        message,
        activeSession.session_type === 'assessment'
      );
      
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      
      if (res.data.audio) {
        playAudio(res.data.audio);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to get response');
    } finally {
      setIsSessionLoading(false);
    }
  };
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
          fullRecordingRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        setIsTranscribing(true);
        try {
          const res = await transcribeAudio(audioBlob);
          const transcript = res.data.text;
          setCurrentTranscript(prev => prev + ' ' + transcript);
          
          if (transcript.trim()) {
            sendMessage(transcript);
          }
        } catch (error) {
          console.error('Transcription failed:', error);
          toast.error('Failed to transcribe audio');
        } finally {
          setIsTranscribing(false);
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Microphone access denied');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  
  const playAudio = (base64Audio) => {
    try {
      const audioData = atob(base64Audio);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      const audioBlob = new Blob([audioArray], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        setIsPlayingAudio(true);
        audioRef.current.onended = () => setIsPlayingAudio(false);
      }
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  };
  
  const handleEndSession = async () => {
    setShowEndSessionDialog(false);
    setIsSessionLoading(true);
    
    try {
      if (fullRecordingRef.current.length > 0) {
        const fullRecording = new Blob(fullRecordingRef.current, { type: 'audio/webm' });
        await uploadSessionRecording(activeSession.id, fullRecording).catch(err => {
          console.warn('Failed to upload recording:', err);
        });
      }
      
      const res = await completeCommunicationSession(activeSession.id, currentTranscript);
      
      setSessionResults(res.data.assessment);
      setShowResultsDialog(true);
      
      setActiveSession(null);
      setMessages([]);
      setSessionTimer(0);
      setCurrentTranscript('');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      loadInitialData();
    } catch (error) {
      console.error('Failed to end session:', error);
      toast.error('Failed to complete session');
    } finally {
      setIsSessionLoading(false);
    }
  };

  // ==================== HOMEWORK FUNCTIONS ====================
  
  const handleViewUser = async (userId) => {
    setSelectedUser(userId);
    setIsLoadingUser(true);
    try {
      const { data } = await getAdminUserHomework(userId);
      setUserDetails(data);
    } catch (error) {
      toast.error('Failed to load user details');
    } finally {
      setIsLoadingUser(false);
    }
  };
  
  const handleAssignTasks = async (userId) => {
    try {
      const { data } = await assignHomeworkFromCoaching(userId);
      toast.success(data.message);
      loadInitialData();
      if (selectedUser === userId) {
        handleViewUser(userId);
      }
    } catch (error) {
      toast.error('Failed to assign tasks');
    }
  };
  
  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    try {
      await deleteHomeworkTask(taskId);
      toast.success('Task deleted');
      if (userDetails) {
        handleViewUser(selectedUser);
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // ==================== ANALYTICS FUNCTIONS ====================
  
  const loadUserPlan = async (userId) => {
    try {
      const res = await getAICoachingPlan(userId);
      setUserPlan(res.data);
      setSelectedCoachingUser(userId);
    } catch (error) {
      toast.error('Failed to load coaching plan');
    }
  };

  // ==================== HELPER FUNCTIONS ====================
  
  const getSkillRadarData = () => {
    const skills = profile?.skill_scores || {};
    return [
      { skill: 'Pronunciation', value: skills.pronunciation || 0, fullMark: 100 },
      { skill: 'Pace', value: skills.pace || 0, fullMark: 100 },
      { skill: 'Clarity', value: skills.clarity || 0, fullMark: 100 },
      { skill: 'Confidence', value: skills.confidence || 0, fullMark: 100 },
      { skill: 'Comprehension', value: skills.comprehension || 0, fullMark: 100 },
    ];
  };
  
  const getProgressChartData = () => {
    const trend = profile?.improvement_trend || [];
    return trend.map((score, index) => ({
      session: index + 1,
      score: score
    }));
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'in_progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'overdue': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };
  
  const getCurrentPhase = () => {
    const currentPhaseNum = progress?.current_phase || profile?.coaching_phase || 1;
    return COACHING_PHASES.find(p => p.phase === currentPhaseNum) || COACHING_PHASES[0];
  };

  // ==================== LOADING STATE ====================
  
  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="ai-comm-coach-loading">
        <Skeleton className="h-24" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // ==================== RENDER ====================
  
  const currentPhase = getCurrentPhase();
  const currentScore = profile?.overall_score || progress?.current_score || 0;
  const totalSessions = profile?.total_sessions || sessions.length || 0;
  const earnedBadgesCount = badges?.earned_badges?.length || 0;
  const totalBadges = 25;

  return (
    <div className="space-y-6" data-testid="ai-comm-coach">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-xl text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
            <MessageCircle className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading">AI Communication Coach</h1>
            <p className="text-purple-100">Practice, learn, and master communication skills</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center bg-white/10 rounded-xl px-4 py-2 backdrop-blur">
            <p className="text-2xl font-bold">{Math.round(currentScore)}</p>
            <p className="text-xs text-purple-200">Current Score</p>
          </div>
          <div className="text-center bg-white/10 rounded-xl px-4 py-2 backdrop-blur">
            <p className="text-2xl font-bold">{totalSessions}</p>
            <p className="text-xs text-purple-200">Sessions</p>
          </div>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={refreshData}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 h-12">
          <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="practice" className="gap-2" data-testid="tab-practice">
            <Mic className="h-4 w-4" />
            <span className="hidden sm:inline">Practice</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2" data-testid="tab-tasks">
            <ClipboardCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Tasks</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2" data-testid="tab-analytics">
            <PieChart className="h-4 w-4" />
            <span className="hidden sm:inline">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2" data-testid="tab-reports">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Reports</span>
          </TabsTrigger>
          <TabsTrigger value="achievements" className="gap-2" data-testid="tab-achievements">
            <Trophy className="h-4 w-4" />
            <span className="hidden sm:inline">Achievements</span>
          </TabsTrigger>
        </TabsList>

        {/* ==================== DASHBOARD TAB ==================== */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-violet-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                    <Target className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{Math.round(currentScore)}</p>
                    <p className="text-xs text-muted-foreground">Overall Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <MessageCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{totalSessions}</p>
                    <p className="text-xs text-muted-foreground">Total Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Award className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{earnedBadgesCount}/{totalBadges}</p>
                    <p className="text-xs text-muted-foreground">Badges Earned</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Flame className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{progress?.current_streak || 0}</p>
                    <p className="text-xs text-muted-foreground">Day Streak</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Call History Insights - Feedback Context */}
          {feedbackContext?.has_feedback && (
            <Card className="border-l-4 border-l-indigo-500">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Phone className="h-5 w-5 text-indigo-500" />
                  Insights from Your Call History
                </CardTitle>
                <CardDescription>
                  Personalized coaching based on {feedbackContext.calls_analyzed} recent calls
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Strengths */}
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                    <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Your Strengths
                    </p>
                    {feedbackContext.common_strengths?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {feedbackContext.common_strengths.map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs bg-emerald-100 dark:bg-emerald-900 border-emerald-300 dark:border-emerald-700">
                            {s}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Keep practicing to identify patterns</p>
                    )}
                  </div>
                  
                  {/* Areas to Improve */}
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Focus Areas
                    </p>
                    {feedbackContext.common_improvements?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {feedbackContext.common_improvements.map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs bg-amber-100 dark:bg-amber-900 border-amber-300 dark:border-amber-700">
                            {s}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Continue calls to find improvement areas</p>
                    )}
                  </div>
                  
                  {/* Coaching Tips */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      Previous Coaching
                    </p>
                    {feedbackContext.coaching_tips?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {feedbackContext.coaching_tips.slice(0, 2).map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs bg-blue-100 dark:bg-blue-900 border-blue-300 dark:border-blue-700">
                            {s}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Tips will appear after more calls</p>
                    )}
                  </div>
                </div>
                
                {/* Average Score */}
                {feedbackContext.avg_call_score > 0 && (
                  <div className="mt-4 flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Average Call Score</span>
                    <span className="font-bold text-lg text-foreground">{feedbackContext.avg_call_score}/100</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Coaching Journey */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Your Coaching Journey
              </CardTitle>
              <CardDescription>Progress through the 6 phases of communication mastery</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-2 mb-4">
                {COACHING_PHASES.map((phase, idx) => {
                  const PhaseIcon = phase.icon;
                  const isActive = currentPhase.phase === phase.phase;
                  const isCompleted = currentPhase.phase > phase.phase;
                  return (
                    <div key={phase.phase} className="flex-1 flex flex-col items-center">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all
                        ${isActive ? `${phase.color} text-white ring-4 ring-offset-2 ring-${phase.color.replace('bg-', '')}` : ''}
                        ${isCompleted ? 'bg-emerald-500 text-white' : ''}
                        ${!isActive && !isCompleted ? 'bg-muted text-muted-foreground' : ''}
                      `}>
                        {isCompleted ? <CheckCircle2 className="h-6 w-6" /> : <PhaseIcon className="h-6 w-6" />}
                      </div>
                      <p className={`text-xs text-center font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {phase.name}
                      </p>
                      {idx < COACHING_PHASES.length - 1 && (
                        <div className={`hidden md:block absolute h-1 w-full top-6 left-1/2 ${isCompleted ? 'bg-emerald-500' : 'bg-muted'}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <currentPhase.icon className={`h-8 w-8 ${currentPhase.color.replace('bg-', 'text-')}`} />
                  <div>
                    <p className="font-semibold text-foreground">Phase {currentPhase.phase}: {currentPhase.name}</p>
                    <p className="text-sm text-muted-foreground">{currentPhase.description}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Skill Radar */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Skill Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={getSkillRadarData()}>
                      <PolarGrid stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <PolarAngleAxis dataKey="skill" tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }} />
                      <Radar name="Score" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.5} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Progress Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Score History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={getProgressChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="session" tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }} />
                      <YAxis domain={[0, 100]} tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1f2937' : '#fff' }} />
                      <Area type="monotone" dataKey="score" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Sessions & Leaderboard */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Recent Sessions
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('practice')}>
                    View All <ChevronRight className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sessions.slice(0, 5).map((session) => (
                    <div key={session.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${session.session_type === 'assessment' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                          {session.session_type === 'assessment' ? 
                            <Target className="h-4 w-4 text-amber-600 dark:text-amber-400" /> : 
                            <MessageCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          }
                        </div>
                        <div>
                          <p className="font-medium text-sm text-foreground capitalize">{session.session_type}</p>
                          <p className="text-xs text-muted-foreground">
                            {session.created_at ? format(new Date(session.created_at), 'MMM d, h:mm a') : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-foreground">{Math.round(session.overall_score || 0)}</p>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                    </div>
                  ))}
                  {sessions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No sessions yet</p>
                      <Button variant="outline" size="sm" className="mt-2" onClick={() => setActiveTab('practice')}>
                        Start Your First Session
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Leaderboard */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Badge Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {leaderboard.slice(0, 5).map((entry, idx) => (
                    <div key={entry.user_id} className={`flex items-center justify-between p-3 rounded-lg ${
                      idx === 0 ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' :
                      idx === 1 ? 'bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700' :
                      idx === 2 ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800' :
                      'bg-muted'
                    }`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0 ? 'bg-amber-500 text-white' :
                          idx === 1 ? 'bg-slate-400 text-white' :
                          idx === 2 ? 'bg-orange-500 text-white' :
                          'bg-muted-foreground/20 text-muted-foreground'
                        }`}>
                          {idx + 1}
                        </span>
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>{entry.user_name?.charAt(0) || '?'}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm text-foreground">{entry.user_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-amber-500" />
                        <span className="font-bold text-foreground">{entry.badge_count}</span>
                      </div>
                    </div>
                  ))}
                  {leaderboard.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Trophy className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No leaderboard data yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ==================== PRACTICE TAB ==================== */}
        <TabsContent value="practice" className="space-y-6">
          {!activeSession ? (
            <>
              {/* Session Start Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-2 border-dashed border-amber-300 dark:border-amber-700 hover:border-amber-500 transition-colors cursor-pointer" onClick={() => startSession('assessment')}>
                  <CardContent className="p-8 text-center">
                    <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-full w-fit mx-auto mb-4">
                      <Target className="h-12 w-12 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Take Assessment</h3>
                    <p className="text-muted-foreground mb-4">
                      Get a baseline evaluation of your communication skills
                    </p>
                    <Button className="bg-amber-500 hover:bg-amber-600" disabled={isSessionLoading}>
                      {isSessionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Target className="h-4 w-4 mr-2" />}
                      Start Assessment
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-2 border-dashed border-blue-300 dark:border-blue-700 hover:border-blue-500 transition-colors cursor-pointer" onClick={() => startSession('practice')}>
                  <CardContent className="p-8 text-center">
                    <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full w-fit mx-auto mb-4">
                      <MessageCircle className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Practice Session</h3>
                    <p className="text-muted-foreground mb-4">
                      Engage in AI-powered conversation practice
                    </p>
                    <Button className="bg-blue-500 hover:bg-blue-600" disabled={isSessionLoading}>
                      {isSessionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                      Start Practice
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Session History */}
              <Card>
                <CardHeader>
                  <CardTitle>Session History</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell>
                            <Badge variant={session.session_type === 'assessment' ? 'default' : 'secondary'}>
                              {session.session_type}
                            </Badge>
                          </TableCell>
                          <TableCell>{session.created_at ? format(new Date(session.created_at), 'MMM d, yyyy h:mm a') : '-'}</TableCell>
                          <TableCell>{session.duration_minutes ? `${session.duration_minutes} min` : '-'}</TableCell>
                          <TableCell className="font-bold">{Math.round(session.overall_score || 0)}</TableCell>
                          <TableCell>
                            <Badge variant={session.status === 'completed' ? 'success' : 'outline'}>
                              {session.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                      {sessions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            No sessions yet. Start your first session above!
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            /* Active Session UI */
            <Card className="h-[calc(100vh-300px)] flex flex-col">
              <CardHeader className="flex-none border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant={activeSession.session_type === 'assessment' ? 'default' : 'secondary'}>
                      {activeSession.session_type}
                    </Badge>
                    <span className="text-muted-foreground">|</span>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono text-foreground">{formatTime(sessionTimer)}</span>
                  </div>
                  <Button variant="destructive" size="sm" onClick={() => setShowEndSessionDialog(true)}>
                    <StopCircle className="h-4 w-4 mr-2" />
                    End Session
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full p-4">
                  <div className="space-y-4">
                    {messages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-4 rounded-2xl ${
                          msg.role === 'user' 
                            ? 'bg-violet-500 text-white rounded-br-none' 
                            : 'bg-muted text-foreground rounded-bl-none'
                        }`}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {isSessionLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted p-4 rounded-2xl rounded-bl-none">
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>
              <div className="flex-none p-4 border-t">
                <div className="flex items-center gap-2">
                  <Button
                    variant={isRecording ? 'destructive' : 'outline'}
                    size="icon"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isTranscribing}
                  >
                    {isTranscribing ? <Loader2 className="h-5 w-5 animate-spin" /> : 
                     isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  </Button>
                  <Input
                    placeholder="Type your message or use voice..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessage(inputMessage)}
                    disabled={isSessionLoading}
                    className="flex-1"
                  />
                  <Button onClick={() => sendMessage(inputMessage)} disabled={!inputMessage.trim() || isSessionLoading}>
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
                {isRecording && (
                  <p className="text-center text-sm text-red-500 mt-2 animate-pulse">
                    Recording... Click mic to stop
                  </p>
                )}
              </div>
              <audio ref={audioRef} className="hidden" />
            </Card>
          )}
        </TabsContent>

        {/* ==================== TASKS TAB ==================== */}
        <TabsContent value="tasks" className="space-y-6">
          {/* Tasks Overview Stats */}
          {homeworkOverview?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{homeworkOverview.summary.completed_tasks}</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{homeworkOverview.summary.pending_tasks}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{homeworkOverview.summary.overdue_tasks}</p>
                      <p className="text-xs text-muted-foreground">Overdue</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{homeworkOverview.summary.total_users || 0}</p>
                      <p className="text-xs text-muted-foreground">Active Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* User Tasks Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                Weekly Tasks by User
              </CardTitle>
              <CardDescription>
                Tasks are auto-generated based on session performance and call analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Pending</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Overdue</TableHead>
                    <TableHead>Completion Rate</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {homeworkOverview?.users?.map((userData) => (
                    <TableRow key={userData.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{userData.user_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{userData.user_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-amber-50 dark:bg-amber-900/20">
                          {userData.pending_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-900/20">
                          {userData.completed_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-red-50 dark:bg-red-900/20">
                          {userData.overdue_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={userData.completion_rate || 0} className="w-20 h-2" />
                          <span className="text-sm">{Math.round(userData.completion_rate || 0)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleViewUser(userData.user_id)}>
                            <Eye className="h-4 w-4 mr-1" /> View
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleAssignTasks(userData.user_id)}>
                            <Sparkles className="h-4 w-4 mr-1" /> Generate
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!homeworkOverview?.users || homeworkOverview.users.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No task data yet. Tasks will be generated based on user sessions.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== ANALYTICS TAB ==================== */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Company-wide Stats */}
          {coachingStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                      <Target className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{Math.round(coachingStats.avg_score || 0)}</p>
                      <p className="text-xs text-muted-foreground">Avg Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{coachingStats.total_users || 0}</p>
                      <p className="text-xs text-muted-foreground">Active Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{coachingStats.improving_users || 0}</p>
                      <p className="text-xs text-muted-foreground">Improving</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                      <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{coachingStats.needs_attention || 0}</p>
                      <p className="text-xs text-muted-foreground">Need Attention</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* User Analytics Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    User Performance Analytics
                  </CardTitle>
                  <CardDescription>Call-based performance metrics and coaching recommendations</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={filters.department_id} onValueChange={(v) => setFilters(prev => ({...prev, department_id: v}))}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Calls</TableHead>
                    <TableHead>Avg Score</TableHead>
                    <TableHead>Trend</TableHead>
                    <TableHead>Top Skill</TableHead>
                    <TableHead>Focus Area</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coachingStats?.user_stats?.filter(u => 
                    filters.department_id === 'all' || u.department_id === filters.department_id
                  ).map((userStat) => (
                    <TableRow key={userStat.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{userStat.user_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{userStat.user_name}</p>
                            <p className="text-xs text-muted-foreground">{userStat.department_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{userStat.total_calls || 0}</TableCell>
                      <TableCell className="font-bold">{Math.round(userStat.avg_score || 0)}</TableCell>
                      <TableCell>
                        {userStat.trend > 0 ? (
                          <span className="flex items-center text-emerald-600">
                            <ArrowUp className="h-4 w-4" /> +{userStat.trend}
                          </span>
                        ) : userStat.trend < 0 ? (
                          <span className="flex items-center text-red-600">
                            <ArrowDown className="h-4 w-4" /> {userStat.trend}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-900/20">
                          {userStat.top_skill || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-amber-50 dark:bg-amber-900/20">
                          {userStat.focus_area || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => loadUserPlan(userStat.user_id)}>
                          <Eye className="h-4 w-4 mr-1" /> Plan
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!coachingStats?.user_stats || coachingStats.user_stats.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No analytics data available yet. User performance will appear as calls are analyzed.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== REPORTS TAB ==================== */}
        <TabsContent value="reports" className="space-y-6">
          {/* Report Controls */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    AI Coach Reports
                  </CardTitle>
                  <CardDescription>Generate and download comprehensive reports</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      setExportingReport(true);
                      try {
                        const res = await exportCoachReportsCSV('user_performance', reportDateRange.start, reportDateRange.end);
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `ai_coach_performance_${new Date().toISOString().split('T')[0]}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('CSV exported successfully');
                      } catch (error) {
                        toast.error('Failed to export CSV');
                      } finally {
                        setExportingReport(false);
                      }
                    }}
                    disabled={exportingReport}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await exportCoachReportsPDF('user_performance', reportDateRange.start, reportDateRange.end);
                        // Open HTML in new window for printing
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(res.data);
                        printWindow.document.close();
                      } catch (error) {
                        toast.error('Failed to generate PDF');
                      }
                    }}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 items-end">
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Start Date</label>
                    <Input
                      type="date"
                      value={reportDateRange.start}
                      onChange={(e) => setReportDateRange(prev => ({ ...prev, start: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">End Date</label>
                    <Input
                      type="date"
                      value={reportDateRange.end}
                      onChange={(e) => setReportDateRange(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    setReportsLoading(true);
                    try {
                      const res = await getCoachReportsSummary({
                        start_date: reportDateRange.start || undefined,
                        end_date: reportDateRange.end || undefined
                      });
                      setReportsSummary(res.data);
                    } catch (error) {
                      toast.error('Failed to load reports');
                    } finally {
                      setReportsLoading(false);
                    }
                  }}
                  disabled={reportsLoading}
                >
                  {reportsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Generate Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {reportsSummary?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-violet-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-violet-500" />
                    <div>
                      <p className="text-2xl font-bold">{reportsSummary.summary.active_users}/{reportsSummary.summary.total_users}</p>
                      <p className="text-xs text-muted-foreground">Active Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold">{reportsSummary.summary.total_sessions}</p>
                      <p className="text-xs text-muted-foreground">Total Sessions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Target className="h-8 w-8 text-emerald-500" />
                    <div>
                      <p className="text-2xl font-bold">{reportsSummary.summary.avg_score}</p>
                      <p className="text-xs text-muted-foreground">Avg Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-8 w-8 text-amber-500" />
                    <div>
                      <p className="text-2xl font-bold">{reportsSummary.summary.max_score}</p>
                      <p className="text-xs text-muted-foreground">Best Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Skill Averages */}
          {reportsSummary?.skill_averages && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Team Skill Averages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(reportsSummary.skill_averages).map(([skill, score]) => (
                    <div key={skill} className="flex items-center gap-4">
                      <span className="w-32 text-sm font-medium capitalize">{skill}</span>
                      <Progress value={score} className="flex-1" />
                      <span className="w-12 text-sm font-bold text-right">{score}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Performers & Most Improved */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top Performers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Sessions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportsSummary?.top_performers?.map((user, idx) => (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-amber-500 text-white' :
                            idx === 1 ? 'bg-slate-400 text-white' :
                            idx === 2 ? 'bg-orange-500 text-white' :
                            'bg-muted'
                          }`}>
                            {idx + 1}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="font-bold">{Math.round(user.score)}</TableCell>
                        <TableCell>{user.sessions}</TableCell>
                      </TableRow>
                    ))}
                    {(!reportsSummary?.top_performers?.length) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          Generate a report to see data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Most Improved */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  Most Improved
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Improvement</TableHead>
                      <TableHead>Current</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportsSummary?.most_improved?.map((user, idx) => (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-emerald-500 text-white' :
                            idx === 1 ? 'bg-teal-500 text-white' :
                            idx === 2 ? 'bg-green-500 text-white' :
                            'bg-muted'
                          }`}>
                            {idx + 1}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>
                          <span className="text-emerald-600 font-bold">+{user.improvement}</span>
                        </TableCell>
                        <TableCell className="font-bold">{Math.round(user.current_score)}</TableCell>
                      </TableRow>
                    ))}
                    {(!reportsSummary?.most_improved?.length) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          Generate a report to see data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Export Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Export Options</CardTitle>
              <CardDescription>Download detailed reports in various formats</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={async () => {
                    setExportingReport(true);
                    try {
                      const res = await exportCoachReportsCSV('user_performance');
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `user_performance_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('User Performance CSV exported');
                    } catch (e) {
                      toast.error('Export failed');
                    } finally {
                      setExportingReport(false);
                    }
                  }}
                  disabled={exportingReport}
                >
                  <Users className="h-6 w-6" />
                  <span className="font-semibold">User Performance</span>
                  <span className="text-xs text-muted-foreground">All users with scores & skills</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={async () => {
                    setExportingReport(true);
                    try {
                      const res = await exportCoachReportsCSV('sessions');
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `sessions_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Sessions CSV exported');
                    } catch (e) {
                      toast.error('Export failed');
                    } finally {
                      setExportingReport(false);
                    }
                  }}
                  disabled={exportingReport}
                >
                  <MessageCircle className="h-6 w-6" />
                  <span className="font-semibold">Session History</span>
                  <span className="text-xs text-muted-foreground">All practice & assessment sessions</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={async () => {
                    setExportingReport(true);
                    try {
                      const res = await exportCoachReportsCSV('assessments');
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `assessments_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Assessments CSV exported');
                    } catch (e) {
                      toast.error('Export failed');
                    } finally {
                      setExportingReport(false);
                    }
                  }}
                  disabled={exportingReport}
                >
                  <Target className="h-6 w-6" />
                  <span className="font-semibold">Assessment Details</span>
                  <span className="text-xs text-muted-foreground">Scores & AI feedback per session</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== ACHIEVEMENTS TAB ==================== */}
        <TabsContent value="achievements" className="space-y-6">
          {/* Badges Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-amber-500" />
                Your Badges
                <Badge variant="secondary" className="ml-2">{earnedBadgesCount} of {totalBadges}</Badge>
              </CardTitle>
              <CardDescription>Collect badges by achieving milestones and improving your skills</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {badges?.earned_badges?.map((badge) => {
                  const IconComponent = BADGE_ICONS[badge.icon] || Award;
                  return (
                    <div key={badge.id} className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-xl text-center border border-amber-200 dark:border-amber-700">
                      <div className="p-3 bg-amber-500 rounded-full w-fit mx-auto mb-2">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <p className="font-semibold text-sm text-foreground">{badge.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                    </div>
                  );
                })}
                {badges?.progress?.filter(p => !p.earned).slice(0, 5).map((badge) => {
                  const IconComponent = BADGE_ICONS[badge.icon] || Award;
                  return (
                    <div key={badge.id} className="p-4 bg-muted rounded-xl text-center border border-dashed">
                      <div className="p-3 bg-muted-foreground/20 rounded-full w-fit mx-auto mb-2">
                        <IconComponent className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="font-semibold text-sm text-muted-foreground">{badge.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{badge.progress_text}</p>
                      <Progress value={badge.progress_percent || 0} className="h-1 mt-2" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Challenges Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-500" />
                Weekly Challenges
              </CardTitle>
              <CardDescription>Complete challenges to earn badges and climb the leaderboard</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...(challenges?.auto_challenges || []), ...(challenges?.admin_challenges || [])].map((challenge) => (
                  <div key={challenge.id} className="p-4 bg-muted rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-foreground">{challenge.title}</h4>
                      <Badge variant={challenge.status === 'completed' ? 'success' : 'secondary'}>
                        {challenge.status === 'completed' ? 'Completed' : 'In Progress'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{challenge.description}</p>
                    <div className="flex items-center gap-2">
                      <Progress value={(challenge.progress / challenge.target_value) * 100} className="flex-1 h-2" />
                      <span className="text-sm font-medium text-foreground">{challenge.progress}/{challenge.target_value}</span>
                    </div>
                  </div>
                ))}
                {(!challenges?.auto_challenges?.length && !challenges?.admin_challenges?.length) && (
                  <div className="col-span-2 text-center py-8 text-muted-foreground">
                    <Zap className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No active challenges. Complete sessions to unlock challenges!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Full Leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Full Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Badges</TableHead>
                    <TableHead>Sessions</TableHead>
                    <TableHead>Best Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.map((entry, idx) => (
                    <TableRow key={entry.user_id} className={idx < 3 ? 'bg-muted/50' : ''}>
                      <TableCell>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? 'bg-amber-500 text-white' :
                          idx === 1 ? 'bg-slate-400 text-white' :
                          idx === 2 ? 'bg-orange-500 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {idx + 1}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{entry.user_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{entry.user_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Award className="h-4 w-4 text-amber-500" />
                          <span className="font-bold">{entry.badge_count}</span>
                        </div>
                      </TableCell>
                      <TableCell>{entry.total_sessions || 0}</TableCell>
                      <TableCell className="font-bold">{entry.best_score || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ==================== DIALOGS ==================== */}
      
      {/* End Session Dialog */}
      <Dialog open={showEndSessionDialog} onOpenChange={setShowEndSessionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Session?</DialogTitle>
            <DialogDescription>
              Are you sure you want to end this session? Your conversation will be analyzed and scored.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndSessionDialog(false)}>Cancel</Button>
            <Button onClick={handleEndSession} disabled={isSessionLoading}>
              {isSessionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              End Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Results Dialog */}
      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Session Complete!
            </DialogTitle>
          </DialogHeader>
          {sessionResults && (
            <div className="space-y-4">
              <div className="text-center p-6 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30 rounded-xl">
                <p className="text-5xl font-bold text-violet-600 dark:text-violet-400">{Math.round(sessionResults.overall_score || 0)}</p>
                <p className="text-muted-foreground">Overall Score</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(sessionResults.skill_scores || {}).map(([skill, score]) => (
                  <div key={skill} className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground capitalize">{skill}</p>
                    <p className="text-lg font-bold text-foreground">{Math.round(score)}</p>
                  </div>
                ))}
              </div>
              
              {sessionResults.feedback && (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium text-foreground mb-1">AI Feedback</p>
                  <p className="text-sm text-muted-foreground">{sessionResults.feedback}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowResultsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Badge Dialog */}
      <Dialog open={showBadgeDialog} onOpenChange={setShowBadgeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 justify-center">
              <Gift className="h-6 w-6 text-amber-500" />
              New Badge Earned!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {newBadges.map((badge) => {
              const IconComponent = BADGE_ICONS[badge.icon] || Award;
              return (
                <div key={badge.id} className="text-center p-6 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-xl">
                  <div className="p-4 bg-amber-500 rounded-full w-fit mx-auto mb-3">
                    <IconComponent className="h-10 w-10 text-white" />
                  </div>
                  <p className="text-xl font-bold text-foreground">{badge.name}</p>
                  <p className="text-muted-foreground">{badge.description}</p>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowBadgeDialog(false)} className="w-full">Awesome!</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>User Tasks Details</DialogTitle>
          </DialogHeader>
          {isLoadingUser ? (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            </div>
          ) : userDetails ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarFallback>{userDetails.user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-foreground">{userDetails.user?.name}</p>
                  <p className="text-sm text-muted-foreground">{userDetails.user?.email}</p>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userDetails.tasks?.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>
                        <p className="font-medium">{task.title}</p>
                        <p className="text-xs text-muted-foreground">{task.skill_focus}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{task.task_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(task.status)}>{task.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {task.deadline ? format(new Date(task.deadline), 'MMM d') : '-'}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* User Coaching Plan Dialog */}
      <Dialog open={!!selectedCoachingUser} onOpenChange={() => setSelectedCoachingUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>AI Coaching Plan</DialogTitle>
          </DialogHeader>
          {userPlan ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-semibold text-foreground mb-2">Summary</h4>
                <p className="text-sm text-muted-foreground">{userPlan.summary}</p>
              </div>
              
              {userPlan.focus_areas?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Focus Areas</h4>
                  <div className="flex flex-wrap gap-2">
                    {userPlan.focus_areas.map((area, idx) => (
                      <Badge key={idx} variant="secondary">{area}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {userPlan.recommendations?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Recommendations</h4>
                  <ul className="space-y-2">
                    {userPlan.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                        <span className="text-muted-foreground">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
